import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gift, Loader2, TrendingUp, TrendingDown, LogOut, Package, ExternalLink, PackageX } from "lucide-react";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rewards, setRewards] = useState(null);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === false) {
      navigate("/login", { replace: true, state: { from: "/account" } });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/account/rewards").then((r) => setRewards(r.data)).catch(() => setRewards(null));
    api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="account-page" className="max-w-3xl mx-auto px-6 py-20 bg-background min-h-screen">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 font-bold">My Account</p>
          <h1 className="font-anton text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-white">
            {user.name || user.email}
          </h1>
          <p className="text-white/50 mt-1 text-sm">{user.email}</p>
        </div>
        <button
          data-testid="account-logout-button"
          onClick={doLogout}
          className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      {/* Rewards */}
      <div className="mt-10 border border-primary/30 bg-primary/5 p-8" data-testid="account-rewards">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-white/50">
          <Gift className="w-4 h-4 text-primary" /> Blend Rewards
        </div>
        <p data-testid="account-points" className="font-anton text-6xl text-primary mt-3">
          {(rewards?.points ?? 0).toLocaleString()}
        </p>
        <p className="text-white/60 uppercase tracking-widest text-xs">points</p>
        <p className="text-sm text-white/70 mt-3">
          Worth <b className="text-primary">{formatPrice(Math.floor((rewards?.points ?? 0) / (rewards?.points_per_dollar_redeem || 200)) * 100)}</b> off your next order · earn {rewards?.points_per_dollar || 10} points per $1
        </p>
      </div>

      {/* Points history */}
      {rewards?.history?.length > 0 && (
        <div className="mt-8" data-testid="account-rewards-history">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-white/50 mb-3">Points activity</p>
          <div className="divide-y divide-white/10 border border-white/10">
            {rewards.history.map((h, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <span className="flex items-center gap-2 text-sm text-white/70">
                  {h.points >= 0 ? <TrendingUp className="w-4 h-4 text-primary" /> : <TrendingDown className="w-4 h-4 text-white/40" />}
                  {h.points >= 0 ? "Earned" : "Redeemed"}
                  {h.order_id && h.order_id !== "seed" && <span className="text-white/30 text-xs">· #{String(h.order_id).slice(0, 8)}</span>}
                </span>
                <span className={`font-bold text-sm ${h.points >= 0 ? "text-primary" : "text-white/50"}`}>
                  {h.points >= 0 ? "+" : ""}{h.points.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order history */}
      <div className="mt-12" data-testid="account-orders">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-white/50 mb-4">Order history</p>
        {orders === null ? (
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        ) : orders.length === 0 ? (
          <div className="border border-white/10 p-8 text-center">
            <Package className="w-8 h-8 text-white/30 mx-auto mb-3" />
            <p className="text-white/50 text-sm">No orders yet.</p>
            <Link to="/#shop" className="inline-block mt-4 text-primary underline underline-offset-4 font-bold uppercase tracking-widest text-sm hover:text-white">Start shopping</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} data-testid={`account-order-${o.id}`} className="border border-white/10 p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-white">#{o.id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-xs text-white/40">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-anton text-xl text-white">{formatPrice(o.total_price)}</p>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">
                      {o.payment_status === "refunded" ? "Refunded" : (o.status || "processing").replace(/_/g, " ")}
                    </p>
                  </div>
                </div>
                <div className="mt-3 text-sm text-white/60">
                  {o.line_items.map((li, i) => (
                    <span key={i} className="block">
                      {li.quantity}× {li.title}
                      {li.custom_name || li.custom_number ? (
                        <span className="text-primary"> · {li.custom_name}{li.custom_number ? ` #${li.custom_number}` : ""}</span>
                      ) : null}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <Link to={`/track?id=${o.id}`} className="inline-flex items-center gap-1.5 text-primary hover:text-white text-xs font-bold uppercase tracking-widest">
                    Track / details <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                  {o.tracking?.url && (
                    <a href={o.tracking.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest">
                      Shipment <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {o.return_status && (
                    <span className="inline-flex items-center gap-1.5 text-amber-300/80 text-xs font-bold uppercase tracking-widest">
                      <PackageX className="w-3.5 h-3.5" /> {o.return_status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
